import type {
  AdminAnnouncement,
  AdminAnnouncementListResult,
  PublishedAnnouncement,
} from "@yahshi/contracts";
import {
  adminAnnouncementCreateSchema,
  adminAnnouncementListQuerySchema,
  adminAnnouncementPublishSchema,
  adminAnnouncementUpdateSchema,
} from "@yahshi/contracts";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import type { AnnouncementApi } from "./AnnouncementApi";

export type AnnouncementApp = { id: string; name: string; code: string; status: string };

const DEMO_NOW = "2026-09-15T12:00:00.000Z";

const fallbackApps: Record<string, AnnouncementApp> = {
  app_yahshi_web: { id: "app_yahshi_web", name: "Yahshi Web", code: "yahshi-web", status: "active" },
  app_yahshi_admin: { id: "app_yahshi_admin", name: "Yahshi Admin", code: "yahshi-admin", status: "active" },
  app_partner_sandbox: { id: "app_partner_sandbox", name: "合作方沙箱", code: "partner-sandbox", status: "disabled" },
};

function announcementWindow(status: AdminAnnouncement["status"], startsAt: string | null, endsAt: string | null, now: Date): AdminAnnouncement["window"] {
  if (status !== "published") return "none";
  if (startsAt && new Date(startsAt) > now) return "scheduled";
  if (endsAt && new Date(endsAt) <= now) return "expired";
  return "current";
}

function withWindow(row: AdminAnnouncement, now: Date): AdminAnnouncement {
  return { ...row, window: announcementWindow(row.status, row.startsAt, row.endsAt, now) };
}

function toPublished(row: AdminAnnouncement): PublishedAnnouncement {
  return {
    id: row.id, appId: row.appId, title: row.title, titleUg: row.titleUg, body: row.body, bodyUg: row.bodyUg,
    startsAt: row.startsAt, endsAt: row.endsAt, publishedAt: row.publishedAt ?? row.updatedAt, version: row.version,
  };
}

function seedRows(nowIso: string): AdminAnnouncement[] {
  return [
    {
      id: "ann_demo_20260915001", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      title: "系统维护通知", titleUg: "سىستېما ئاسراش ئۇقتۇرۇشى",
      body: "9 月 16 日 02:00–04:00 进行系统维护。维护期间部分接口可能短暂不可用。",
      bodyUg: "16-سېنتەبىر 02:00–04:00 سىستېما ئاسراش ئېلىپ بېرىلىدۇ. بۇ مەزگىلدە بەزى ئېغىزلار قىسقا ۋاقىت ئىشلىمەسلىكى مۇمكىن.",
      status: "published", startsAt: "2026-09-14T00:00:00.000Z", endsAt: "2026-09-20T00:00:00.000Z",
      window: "current", publishedAt: "2026-09-14T02:00:00.000Z", version: 2,
      createdAt: "2026-09-13T08:00:00.000Z", updatedAt: nowIso,
    },
    {
      id: "ann_demo_20260915002", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      title: "会员日预告", titleUg: "ئەزا كۈنى ئالدىن ئۇقتۇرۇشى", body: "会员日将于 9 月 18 日开始，具体权益由产品端解释。", bodyUg: "ئەزا كۈنى 18-سېنتەبىردىن باشلىنىدۇ، كونكرېت ھوقۇق-مەنپەئەتنى مەھسۇلات تەرىپى چۈشەندۈرىدۇ.",
      status: "published", startsAt: "2026-09-18T00:00:00.000Z", endsAt: null,
      window: "scheduled", publishedAt: "2026-09-15T01:00:00.000Z", version: 1,
      createdAt: "2026-09-15T01:00:00.000Z", updatedAt: "2026-09-15T01:00:00.000Z",
    },
    {
      id: "ann_demo_20260915003", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      title: "开学季活动", titleUg: "مەكتەپ باشلاش پائالىيىتى", body: "开学季活动已结束，本条仅作历史展示。", bodyUg: "مەكتەپ باشلاش پائالىيىتى ئاخىرلاشتى، بۇ پەقەت تارىخىي كۆرسىتىش.",
      status: "published", startsAt: null, endsAt: "2026-09-10T00:00:00.000Z",
      window: "expired", publishedAt: "2026-09-01T00:00:00.000Z", version: 1,
      createdAt: "2026-08-28T00:00:00.000Z", updatedAt: "2026-09-01T00:00:00.000Z",
    },
    {
      id: "ann_demo_20260915004", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      title: "草稿：新功能说明", titleUg: "دەسلەپكى نۇسخا: يېڭى ئىقتىدار", body: "尚未发布。产品端和 H5 都不会读到草稿。", bodyUg: "تېخى ئېلان قىلىنمىدى. مەھسۇلات تەرىپى ۋە H5 دەسلەپكى نۇسخىنى ئوقۇمايدۇ.",
      status: "draft", startsAt: null, endsAt: null,
      window: "none", publishedAt: null, version: 1,
      createdAt: "2026-09-15T03:00:00.000Z", updatedAt: "2026-09-15T03:00:00.000Z",
    },
  ];
}

export function createDevelopmentAnnouncementApi(options: {
  getApp?: (id: string) => Promise<AnnouncementApp>;
  now?: () => Date;
} = {}): AnnouncementApi {
  const now = options.now ?? (() => new Date(DEMO_NOW));
  let rows = seedRows(now().toISOString()).map((row) => withWindow(row, now()));
  const lookup = async (id: string): Promise<AnnouncementApp> => {
    if (options.getApp) return options.getApp(id);
    const fallback = fallbackApps[id];
    if (!fallback) throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
    return fallback;
  };
  const requireRow = (id: string) => {
    const row = rows.find((item) => item.id === id);
    if (!row) throw new M1ApiError("ANNOUNCEMENT_NOT_FOUND", "公告不存在或未在生效窗口");
    return row;
  };
  const replace = (next: AdminAnnouncement) => {
    const stamped = withWindow({ ...next, updatedAt: now().toISOString() }, now());
    rows = rows.map((item) => item.id === stamped.id ? stamped : item);
    return stamped;
  };
  return {
    async list(query = {}) {
      const parsed = adminAnnouncementListQuerySchema.parse(query);
      const at = now();
      const scopedRows = await Promise.all(rows.map(async row => { const app = await lookup(row.appId);return {...withWindow(row, at),appName:app.name,appCode:app.code,...(app.status!=="active"?{window:"unavailable" as const}:{})}; }));
      const base = scopedRows.filter((row) => {
        const keyword = parsed.keyword.trim().toLowerCase();
        const matchesKeyword = !keyword
          || row.title.toLowerCase().includes(keyword)
          || row.titleUg.toLowerCase().includes(keyword)
          || row.id.toLowerCase().includes(keyword)
          || row.appName.toLowerCase().includes(keyword)
          || row.appCode.toLowerCase().includes(keyword);
        const matchesStatus = parsed.status === "all" || row.status === parsed.status;
        const matchesApp = !parsed.appId || row.appId === parsed.appId;
        return matchesKeyword && matchesStatus && matchesApp;
      });
      const matched = base.filter((row) => parsed.window === "all" || row.window === parsed.window);
      const start = (parsed.page - 1) * parsed.pageSize;
      return {
        items: matched.slice(start, start + parsed.pageSize),
        total: matched.length,
        summary: {
          total: base.length,
          current: base.filter((row) => row.window === "current").length,
          scheduled: base.filter((row) => row.window === "scheduled").length,
          expired: base.filter((row) => row.window === "expired").length,
          draft: base.filter((row) => row.status === "draft").length,
          unavailable: base.filter(row=>row.window==="unavailable").length,
        },
      } satisfies AdminAnnouncementListResult;
    },
    async get(id) {
      const row=requireRow(id),app=await lookup(row.appId);
      return {...withWindow(row,now()),appName:app.name,appCode:app.code,...(app.status!=="active"?{window:"unavailable" as const}:{})};
    },
    async create(input) {
      const parsed = adminAnnouncementCreateSchema.safeParse(input);
      if (!parsed.success) throw new M1ApiError("VALIDATION_ERROR", parsed.error.issues[0]?.message ?? "请检查公告参数");
      const app = await lookup(parsed.data.appId);
      const created: AdminAnnouncement = {
        id: `ann_${crypto.randomUUID().replaceAll("-", "").slice(0, 24)}`,
        appId: app.id, appName: app.name, appCode: app.code,
        title: parsed.data.title, titleUg: parsed.data.titleUg, body: parsed.data.body, bodyUg: parsed.data.bodyUg, status: "draft",
        startsAt: parsed.data.startsAt, endsAt: parsed.data.endsAt, window: "none",
        publishedAt: null, version: 1, createdAt: now().toISOString(), updatedAt: now().toISOString(),
      };
      rows = [created, ...rows];
      return created;
    },
    async update(id, input) {
      const parsed = adminAnnouncementUpdateSchema.safeParse(input);
      if (!parsed.success) throw new M1ApiError("VALIDATION_ERROR", parsed.error.issues[0]?.message ?? "请检查公告参数");
      const row = requireRow(id);
      if (row.version !== parsed.data.expectedVersion) throw new M1ApiError("VERSION_CONFLICT", "公告已被其他人更新，请刷新后重试");
      return replace({
        ...row, title: parsed.data.title, titleUg: parsed.data.titleUg, body: parsed.data.body, bodyUg: parsed.data.bodyUg, startsAt: parsed.data.startsAt, endsAt: parsed.data.endsAt, version: row.version + 1,
      });
    },
    async publish(id, input) {
      const parsed = adminAnnouncementPublishSchema.parse(input);
      const row = requireRow(id);
      if (row.version !== parsed.expectedVersion) throw new M1ApiError("VERSION_CONFLICT", "公告已被其他人更新，请刷新后重试");
      if (row.status === "published") throw new M1ApiError("ANNOUNCEMENT_INVALID_TRANSITION", "当前状态不能执行该操作");
      const app = await lookup(row.appId);
      if (app.status !== "active") throw new M1ApiError("APP_DISABLED", "应用已停用");
      return replace({
        ...row, status: "published", publishedAt: now().toISOString(), version: row.version + 1,
      });
    },
    async withdraw(id, input) {
      const parsed = adminAnnouncementPublishSchema.parse(input);
      const row = requireRow(id);
      if (row.version !== parsed.expectedVersion) throw new M1ApiError("VERSION_CONFLICT", "公告已被其他人更新，请刷新后重试");
      if (row.status !== "published") throw new M1ApiError("ANNOUNCEMENT_INVALID_TRANSITION", "当前状态不能执行该操作");
      return replace({ ...row, status: "withdrawn", version: row.version + 1 });
    },
    async listPublished(appId) {
      const app = await lookup(appId);
      if (app.status !== "active") throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
      const at = now();
      return rows
        .map((row) => withWindow(row, at))
        .filter((row) => row.appId === appId && row.window === "current")
        .sort((left, right) => (right.publishedAt ?? "").localeCompare(left.publishedAt ?? "") || right.id.localeCompare(left.id))
        .map(toPublished);
    },
    async getPublished(appId, id) {
      const items = await this.listPublished(appId);
      const found = items.find((item) => item.id === id);
      if (!found) throw new M1ApiError("ANNOUNCEMENT_NOT_FOUND", "公告不存在或未在生效窗口");
      return found;
    },
  };
}
